import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Container, Form, Grid, Header, Loader, Segment } from 'semantic-ui-react';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import '../css/LTIPlayerLauncher.less';
import '../css/PlayerLaunch.less';

import Auth from '../managers/AuthManager';
import { PDF_DELIVERY_FORMATS } from '../managers/AssignmentManager';
import { CONTENT_ITEM_TYPES, CONTENT_MODE, PLAYER_TYPES } from '../managers/ContentManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';

import PrivatePreviewService, { PRIVATE_PREVIEW_SESSION_KEY } from '../services/PrivatePreviewService';

import { setSessionStorageItem } from '../utils/session';

import { register } from '../i18n';

const t = register('PrivatePreviewView');

/**
 * PrivatePreviewView
 *
 * Route handler at /privatePreview. Receives a deep link with `?contentItemId=...`.
 *
 * If not authenticated, stashes the contentItemId in sessionStorage under
 * `c2c_privatePreviewContentItemId` and redirects to /signin. After login,
 * InitService clears the key and sends teachers/admins back here.
 *
 * If authenticated, resolves the content item type via api/viewContentItem and
 * renders the matching teacher preview player. Students are not allowed.
 * Closing the player returns the user to the dashboard.
 */
export default
@inject('contentManager', 'navigationManager', 'userManager')
@observer
class PrivatePreviewView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
      errorTitle: null,
      isLoading: true,
      playerState: null
    };
    this.ContentPreviewDialog = SatCoreComponent('ContentPreviewDialog');
    this.DocReaderModal = SatCoreComponent('DocReaderModal');
    this.FileViewerModal = SatCoreComponent('FileViewerModal');
    this.FullscreenModal = SatCoreComponent('FullscreenModal');
    this.LearnosityPlayerModal = SatCoreComponent('LearnosityPlayerModal');
    this.Logo = SatCoreComponent('Logo');
  }

  async componentDidMount() {
    const { history, location, navigationManager } = this.props;
    navigationManager.setView(VIEW_SELECTION.LOGIN);

    const urlParams = new URLSearchParams(location.search);
    let contentItemId = null;
    if (urlParams.has('contentItemId') && urlParams.get('contentItemId') !== 'null') {
      contentItemId = urlParams.get('contentItemId');
    }

    if (!contentItemId) {
      this.verificationError(t('errorTitle', 'Error'), t('notAuthorized', 'Not Authorized'));
      return;
    }

    if (!Auth.loggedIn()) {
      try {
        setSessionStorageItem(PRIVATE_PREVIEW_SESSION_KEY, contentItemId);
        console.info('[PrivatePreview] Saved c2c_privatePreviewContentItemId to sessionStorage', { contentItemId });
      } catch (storageError) {
        console.error(storageError);
        this.verificationError(t('errorTitle', 'Error'), t('notAuthorized', 'Not Authorized'));
        return;
      }
      history.replace('/signin');
      return;
    }

    await this.resolvePreview(contentItemId);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handlePlayerMessage);
  }

  resolvePreview = async (contentItemId) => {
    const { contentManager, userManager } = this.props;

    try {
      await userManager.checkUser();

      if (!PrivatePreviewService.canUsePrivatePreview()) {
        this.verificationError(t('errorTitle', 'Error'), t('notAuthorized', 'Not Authorized'));
        return;
      }

      const item = await contentManager.fetchContentItem(contentItemId);
      if (!item) {
        this.verificationError(t('errorTitle', 'Error'), t('contentNotFound', 'The requested content could not be found.'));
        return;
      }

      const { entityTypeId } = item;
      const contentName = item.friendlyName || item.name || '';

      let pdfDeliveryFormat = null;
      if (entityTypeId === CONTENT_ITEM_TYPES.PDF_RESOURCE) {
        await contentManager.fetchReaderConfig();
        pdfDeliveryFormat = contentManager.docReaderId
          ? PDF_DELIVERY_FORMATS.DOCREADER
          : PDF_DELIVERY_FORMATS.FLOWPAPER;
      }

      const option = await contentManager.getOptionsForTeacherPreview(
        contentItemId, entityTypeId, pdfDeliveryFormat, window.location.origin,
        userManager.canViewAsTeacher, userManager.userId
      );

      if (!option) {
        this.verificationError(t('errorTitle', 'Error'), t('notAuthorized', 'Not Authorized'));
        return;
      }

      const { playerType, viewUrl, isFlowpaper } = option;

      if (!playerType && viewUrl) {
        window.location.replace(viewUrl);
        return;
      }

      if (!playerType && !viewUrl) {
        if (entityTypeId === CONTENT_ITEM_TYPES.FILE_RESOURCE) {
          this.verificationError(
            t('downloadOnlyTitle', 'Download Only'),
            t('downloadOnlyModalMsg', 'This resource is available for download only.')
          );
        } else {
          this.verificationError(
            t('noPreviewAvailableTitle', 'No Preview Available'),
            t('noPreviewAvailableMsg', 'A preview is not available for this resource.')
          );
        }
        return;
      }

      if (playerType === PLAYER_TYPES.LESSON_PLAYER) {
        contentManager.setLessonPlayerShowing(true);
      }
      await contentManager.configPlayerWindow(playerType, window, this.handlePlayerMessage);

      this.setState({
        isLoading: false,
        playerState: {
          contentItemId,
          contentMode: CONTENT_MODE.PREVIEW,
          contentName,
          contentType: entityTypeId,
          contentUrl: viewUrl,
          isFlowpaper,
          playerType
        }
      });
    } catch (error) {
      console.error('[PrivatePreview] Failed to resolve preview', error);
      this.verificationError(t('errorTitle', 'Error'), t('previewFailed', 'Unable to load the requested preview.'));
    }
  }

  handlePlayerMessage = (event) => {
    if (event.data === 'hideIframe' || event.data === 'closePressed') {
      this.handleClose();
    }
  }

  handleClose = () => {
    const { contentManager, history } = this.props;
    window.removeEventListener('message', this.handlePlayerMessage);
    contentManager.setLessonPlayerShowing(false);
    this.setState({ playerState: null });
    history.replace(`/?view=${VIEW_SELECTION.DASHBOARD}`);
  }

  verificationError = (errorTitle, errorMessage) => {
    this.setState({ errorMessage, errorTitle, isLoading: false });
  }

  failPanel = () => {
    const { errorTitle, errorMessage } = this.state;
    const { Logo } = this;
    return (
      <Grid className='fullHeight' textAlign='center' verticalAlign='middle'>
        <Grid.Column className='max-width-558'>
          <Form>
            <Header as='h2' attached='top' block>
              <div className='message-header'>
                <Logo />
                <div>{errorTitle}</div>
              </div>
            </Header>
            <Segment attached>
              <Form.Field>
                <Container className='login-error-message' text>
                  {errorMessage}
                </Container>
              </Form.Field>
            </Segment>
          </Form>
        </Grid.Column>
      </Grid>
    );
  }

  renderLessonPlayer = () => {
    const { FullscreenModal } = this;
    const { userManager } = this.props;
    const { playerState } = this.state;
    return (
      <div className='course-content'>
        <FullscreenModal
          className='lesson-player'
          closeIframeCallback={this.handleClose}
          contentImageUrl=''
          contentName={playerState.contentName}
          isTeacher={userManager.isTeacher}
          page='lesson-player'
          url={playerState.contentUrl} />
      </div>
    );
  }

  renderFileViewer = () => {
    const { FileViewerModal } = this;
    const { userManager } = this.props;
    const { playerState } = this.state;
    return (
      <div className='course-content'>
        <FileViewerModal
          closeModalCallback={this.handleClose}
          contentImageUrl=''
          contentMode={playerState.contentMode}
          contentName={playerState.contentName}
          contentType={playerState.contentType}
          instruction=''
          isFlowpaper={playerState.isFlowpaper}
          isTeacher={userManager.isTeacher}
          page='file-viewer'
          resourceName={playerState.contentName}
          url={playerState.contentUrl} />
      </div>
    );
  }

  renderDocreaderViewer = () => {
    const { DocReaderModal } = this;
    const { contentManager, userManager } = this.props;
    const { playerState } = this.state;
    return (
      <div className='course-content'>
        <DocReaderModal
          closeModalCallback={this.handleClose}
          contentImageUrl=''
          contentMode={playerState.contentMode}
          contentName={playerState.contentName}
          contentType={playerState.contentType}
          instruction=''
          isFlowpaper={playerState.isFlowpaper}
          isTeacher={userManager.isTeacher}
          page='docreader-viewer'
          resourceName={playerState.contentName}
          sessionId={contentManager.currentflowPaperSessionId}
          url={playerState.contentUrl} />
      </div>
    );
  }

  renderLearnosityPlayer = () => {
    const { LearnosityPlayerModal } = this;
    const { history, userManager } = this.props;
    const { playerState } = this.state;
    return (
      <div className='course-content'>
        <LearnosityPlayerModal
          closeModalCallback={this.handleClose}
          contentImageUrl=''
          contentItemId={playerState.contentItemId}
          contentMode={playerState.contentMode}
          contentName={playerState.contentName}
          history={history}
          instruction=''
          isTeacher={userManager.isTeacher}
          page='learnosity-player'
          resourceName={playerState.contentName} />
      </div>
    );
  }

  renderContentPreviewDialog = () => {
    const { ContentPreviewDialog } = this;
    const { playerState } = this.state;
    return (
      <ContentPreviewDialog
        close={this.handleClose}
        contentItemId={playerState.contentItemId}
        contentItemType={playerState.contentType}
        open
        resourceName={playerState.contentName} />
    );
  }

  render() {
    const { errorMessage, isLoading, playerState } = this.state;

    if (errorMessage) {
      return (
        <div className='lrn-player-launcher-container student-assignment-launcher-container'>
          {this.failPanel()}
        </div>
      );
    }

    if (isLoading || !playerState) {
      return (
        <div className='PlayerLaunch'>
          <div className='player-launch-loading'>
            <div className='loading-message-container'>
              <Loader active inline size='large' />
              <span>{t('loadingPreview', 'Loading preview…')}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='PlayerLaunch'>
        {(() => {
          switch (playerState.playerType) {
            case PLAYER_TYPES.LESSON_PLAYER:
              return this.renderLessonPlayer();
            case PLAYER_TYPES.FILE_VIEWER:
              return this.renderFileViewer();
            case PLAYER_TYPES.DOCREADER_VIEWER:
              return this.renderDocreaderViewer();
            case PLAYER_TYPES.LEARNOSITY_PLAYER:
              return this.renderLearnosityPlayer();
            case PLAYER_TYPES.CONTENT_PREVIEW_PLAYER:
              return this.renderContentPreviewDialog();
            default:
              return null;
          }
        })()}
      </div>
    );
  }
}

SatCoreRegister('PrivatePreviewView', PrivatePreviewView);
